// SPDX-License-Identifier: AGPL-3.0-or-later

import {Endpoints} from '@app/features/app/constants/Endpoints';
import * as ImageCacheUtils from '@app/features/messaging/utils/ImageCacheUtils';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	STREAM_PREVIEW_CONTENT_TYPE_JPEG,
	STREAM_PREVIEW_REFRESH_INTERVAL_MS,
} from '@fluxer/constants/src/StreamConstants';
import {useCallback, useEffect, useRef, useState} from 'react';

const logger = new Logger('useStreamPreview');
const previewInflight = new Map<string, Promise<string | null>>();
const previewCache = new Map<
	string,
	{
		url: string;
		fetchedAt: number;
	}
>();
const PREVIEW_CACHE_MAX = 24;

interface StreamPreviewState {
	previewUrl: string | null;
	isPreviewLoading: boolean;
}

const isCacheFresh = (fetchedAt: number) => Date.now() - fetchedAt < STREAM_PREVIEW_REFRESH_INTERVAL_MS;
const touchCache = (
	streamKey: string,
	entry: {
		url: string;
		fetchedAt: number;
	},
) => {
	previewCache.delete(streamKey);
	previewCache.set(streamKey, entry);
};
const getCachedPreview = (streamKey: string) => {
	const entry = previewCache.get(streamKey);
	if (!entry) return null;
	if (!isCacheFresh(entry.fetchedAt)) return null;
	touchCache(streamKey, entry);
	return entry.url;
};
const getAnyCachedPreview = (streamKey: string) => previewCache.get(streamKey)?.url ?? null;
const setCachedPreview = (streamKey: string, url: string | null) => {
	if (!url) return;
	const existing = previewCache.get(streamKey);
	if (existing && existing.url !== url) {
		URL.revokeObjectURL(existing.url);
	}
	const entry = {url, fetchedAt: Date.now()};
	touchCache(streamKey, entry);
	ImageCacheUtils.loadImage(url, () => {});
	if (previewCache.size <= PREVIEW_CACHE_MAX) return;
	const [oldestKey, oldestEntry] = previewCache.entries().next().value as [
		string,
		{
			url: string;
			fetchedAt: number;
		},
	];
	previewCache.delete(oldestKey);
	URL.revokeObjectURL(oldestEntry.url);
};

export function useStreamPreview(enabled: boolean, streamKey: string): StreamPreviewState {
	const [previewUrl, setPreviewUrl] = useState<string | null>(() => (enabled ? getAnyCachedPreview(streamKey) : null));
	const [loading, setLoading] = useState(false);
	const disposedRef = useRef(false);
	const fetchGenerationRef = useRef(0);
	const fetchPreview = useCallback(async () => {
		const generation = ++fetchGenerationRef.current;
		const shouldApply = () => !disposedRef.current && fetchGenerationRef.current === generation;
		const cached = getCachedPreview(streamKey);
		if (cached) {
			if (shouldApply()) {
				setLoading(false);
				setPreviewUrl(cached);
			}
			return;
		}
		const staleEntry = previewCache.get(streamKey);
		const staleUrl = staleEntry?.url ?? null;
		const existing = previewInflight.get(streamKey);
		if (existing) {
			if (shouldApply()) {
				setLoading(true);
			}
			const url = await existing;
			if (url) {
				setCachedPreview(streamKey, url);
			}
			if (shouldApply()) {
				setLoading(false);
				setPreviewUrl(url ?? staleUrl);
			}
			return;
		}
		const p = (async () => {
			if (shouldApply()) {
				setLoading(true);
			}
			try {
				const response = await http.get<ArrayBuffer>(Endpoints.STREAM_PREVIEW(streamKey), {
					parse: 'binary',
				});
				logger.debug('useStreamPreview: HTTP response', {
					ok: response.ok,
					status: response.status,
					hasBody: !!response.body,
					streamKey,
				});
				if (!response.ok || !response.body) return null;
				const contentType = response.headers['content-type'] || STREAM_PREVIEW_CONTENT_TYPE_JPEG;
				const blob = new Blob([response.body], {type: contentType});
				const url = URL.createObjectURL(blob);
				setCachedPreview(streamKey, url);
				return url;
			} catch (err) {
				logger.error('preview fetch failed', err);
				return null;
			} finally {
				if (shouldApply()) {
					setLoading(false);
				}
			}
		})();
		previewInflight.set(streamKey, p);
		const url = await p.finally(() => {
			if (previewInflight.get(streamKey) === p) {
				previewInflight.delete(streamKey);
			}
		});
		logger.debug('useStreamPreview: fetch result', {url: url ? 'got url' : 'null', streamKey});
		if (shouldApply()) {
			setPreviewUrl(url ?? staleUrl);
		}
	}, [streamKey]);
	useEffect(() => {
		disposedRef.current = false;
		return () => {
			disposedRef.current = true;
			fetchGenerationRef.current++;
		};
	}, []);
	useEffect(() => {
		if (!enabled) return;
		void fetchPreview();
		const intervalId = setInterval(() => {
			void fetchPreview();
		}, STREAM_PREVIEW_REFRESH_INTERVAL_MS);
		return () => {
			clearInterval(intervalId);
			fetchGenerationRef.current++;
		};
	}, [enabled, fetchPreview]);
	useEffect(() => {
		if (enabled) return;
		fetchGenerationRef.current++;
		setLoading(false);
		setPreviewUrl(null);
	}, [enabled]);
	return {previewUrl, isPreviewLoading: loading};
}
